import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Banner, BannerCreation, BannerUpdate } from '../models/banner.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private _http = inject(HttpClient);
  protected authService = inject(AuthService);

  getBanner(id: number): Observable<Banner> {
    const url = this.authService.isSuperAdmin() ? '/api/banner?adminId=' + id : '/api/banner';

    return this._http.get<{ data: Banner; statusCode: number }>(url).pipe(
      map(res => {
        return res.data;
      }),
    );
  }

  getBannerOrNull(id: number): Observable<Banner | null> {
    return this.getBanner(id).pipe(catchError(error => (isBannerNotFound(error) ? of(null) : throwError(() => error))));
  }

  addBanner(newBanner: BannerCreation): Observable<EmptyMutationResponse> {
    return this._http
      .post<void>(
        '/api/banner',
        { adminId: newBanner.adminId, file: newBanner.picture },
        {
          headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
          observe: 'response',
        },
      )
      .pipe(map(toEmptyMutationResponse));
  }

  editBanner(banner: BannerUpdate): Observable<EmptyMutationResponse> {
    return this._http
      .put<void>(
        `/api/banner`,
        { id: banner.id, file: banner.picture },
        {
          headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
          observe: 'response',
        },
      )
      .pipe(map(toEmptyMutationResponse));
  }

  deleteBanner(id: Banner['id']): Observable<EmptyMutationResponse> {
    return this._http.delete<void>(`/api/banner/${id}`, { observe: 'response' }).pipe(map(toEmptyMutationResponse));
  }
}

type EmptyMutationResponse = { statusCode: number };

const toEmptyMutationResponse = (response: HttpResponse<unknown>): EmptyMutationResponse => ({ statusCode: response.status });

const isBannerNotFound = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const status =
    'statusCode' in error && typeof error.statusCode === 'number'
      ? error.statusCode
      : 'status' in error && typeof error.status === 'number'
        ? error.status
        : undefined;
  if (status !== 404) {
    return false;
  }

  const detail =
    'data' in error && error.data && typeof error.data === 'object' && 'error' in error.data
      ? error.data.error
      : 'error' in error
        ? error.error
        : undefined;

  return (
    detail === 'BANNER_NOT_FOUND' || (!!detail && typeof detail === 'object' && 'error' in detail && detail.error === 'BANNER_NOT_FOUND')
  );
};
